import {Card, type FormControl} from 'platform/components';
import {Box, Hide, HStack, Integer, Show, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';
import {Outlet} from 'react-router-dom';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FilterContextType} from '../../../hooks/FilterContext';
import {useHeaderHeight} from '../../../hooks/useHeaderHeight';
import {FilterFormData} from '../../../types/FilterFormData';
import {PriceReportType} from '../../../types/PriceReportType';
import {SavedFilters} from '../../Filter/components/SavedFilters/SavedFilters';
import {Filter} from '../../Filter/Filter';
import {formValuesToRequestValues} from '../../Filter/utils/formValuesToRequestValues';
import {StickyContainer} from '../../StickyContainer/StickyContainer';
import {useLookupDefaultAddress} from '../hooks/useLookupDefaultAddress';
import {getDefaultValuesWithResolvedAddress} from '../utils/getDefaultValuesWithResolvedAddress';
import {FilterModal} from './FilterModal';

type FilterLayoutFormProps = TestIdProps & {
  clearDebug: () => void;
  context: FilterContextType;
  control: FormControl<FilterFormData>;
  formApi: UseFormReturn<FilterFormData>;
  onSelectFilters: (filters: FilterFormData | null) => void;
  priceReport: PriceReportType;
  showSavedFilters: boolean;
};

export function FilterLayoutForm(props: FilterLayoutFormProps) {
  const headerHeight = useHeaderHeight();
  const resolvedDefaultAddress = useLookupDefaultAddress({
    formApi: props.formApi,
    priceReport: props.priceReport,
  });

  const defaultValues = getDefaultValuesWithResolvedAddress(
    props.priceReport,
    resolvedDefaultAddress
  );

  const onReset = () => {
    props.formApi.reset(defaultValues);
    props.context.onFilterChange(formValuesToRequestValues(defaultValues));
    props.context.setErrors(null);
    props.onSelectFilters(null);
    props.clearDebug();
  };

  return (
    <>
      <HStack>
        <Hide onMobile onTablet>
          <Box width={80} flexShrink={0} padding={2} paddingRight={0}>
            <StickyContainer offset={2}>
              <VStack spacing={3}>
                <Show when={props.showSavedFilters}>
                  <Card>
                    <SavedFilters
                      priceReport={props.priceReport}
                      formApi={props.formApi}
                      onFilterChange={() => props.control.onChange?.()}
                      data-testid={suffixTestId('savedFilters', props)}
                    />
                  </Card>
                </Show>
                <Card>
                  <Filter
                    priceReport={props.priceReport}
                    formControl={props.control}
                    formApi={props.formApi}
                    onReset={onReset}
                    isSavedFiltersExternal={props.showSavedFilters}
                    data-testid="filter"
                  />
                </Card>
              </VStack>
            </StickyContainer>
          </Box>
        </Hide>
        <Box flexGrow={1} minWidth={0}>
          <Box
            position={[undefined, undefined, 'sticky']}
            top={(headerHeight / 4) as Integer}
            data-testid={suffixTestId('stickyContainer', props)}
          >
            <Outlet />
          </Box>
        </Box>
      </HStack>
      <Show onMobile onTablet>
        <FilterModal
          priceReport={props.priceReport}
          formControl={props.control}
          formApi={props.formApi}
          onReset={onReset}
          isSavedFiltersExternal={props.showSavedFilters}
          data-testid="filterModal"
        />
      </Show>
    </>
  );
}
